import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Tags, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, Confirm } from "@/components/common";
import { api, errMsg } from "@/lib/api";

export default function AdminCategories() {
  const { data, loading, error, reload } = useApi("/admin/categories");
  const [name, setName] = useState("");
  const [edit, setEdit] = useState(null);
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const add = (e) => { e.preventDefault(); run(() => api.post("/admin/categories", { name }), "Category created").then(() => setName("")); };
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Taxonomy" title="Categories" desc="Every creator has one primary category." />
      <form onSubmit={add} className="flex gap-2 mb-6">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" className="h-11 rounded-xl bg-card" data-testid="new-category-input" />
        <Button disabled={!name.trim()} className="rounded-full h-11 px-5" data-testid="create-category-button"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
      </form>
      {loading ? <LoadingGrid count={6} className="space-y-2" h="h-14" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.length === 0 ? <EmptyState icon={Tags} title="No categories yet" /> : (
          <div className="uc-card divide-y divide-border" data-testid="categories-list">
            {data.map((c) => (
              <div key={c.id} className="flex items-center gap-3 px-4 py-3" data-testid={`category-row-${c.name.toLowerCase()}`}>
                {edit?.id === c.id ? (
                  <>
                    <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="h-9 rounded-lg bg-background" data-testid="edit-category-input" />
                    <Button size="icon" variant="ghost" onClick={() => run(() => api.put(`/admin/categories/${c.id}`, { name: edit.name }), "Category renamed").then(() => setEdit(null))} data-testid="save-category-button"><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEdit(null)}><X className="h-4 w-4" /></Button>
                  </>
                ) : (
                  <>
                    <Link to={`/admin/creators?category=${c.id}`} className="flex-1 font-medium hover:text-primary">{c.name}</Link>
                    <span className="num text-sm text-muted-foreground" data-testid={`category-count-${c.name.toLowerCase()}`}>{c.creator_count} creators</span>
                    <Button size="icon" variant="ghost" className="rounded-full" onClick={() => setEdit({ id: c.id, name: c.name })} data-testid={`edit-category-${c.name.toLowerCase()}`}><Pencil className="h-4 w-4" /></Button>
                    <Confirm title={`Delete ${c.name}?`} desc={c.creator_count ? "Reassign its creators first." : "This cannot be undone."} destructive confirmLabel="Delete" testid={`delete-category-confirm-${c.name.toLowerCase()}`}
                      onConfirm={() => run(() => api.delete(`/admin/categories/${c.id}`), "Category deleted")}
                      trigger={<Button size="icon" variant="ghost" className="rounded-full text-red-400" data-testid={`delete-category-${c.name.toLowerCase()}`}><Trash2 className="h-4 w-4" /></Button>} />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
